import { useMemo } from 'react'
import {
  FormGrid,
  Label,
  PresetButton,
  StyledAsyncSelect,
  StyledInput,
  StyledSelect,
  StyledTextarea,
} from './DieticianConsultationSubSection.styled'
import { useFieldMap } from '../../hooks/useFieldMap'
import { useLoadOptions } from '../../hooks/useLoadOptions'

export default function DieticianConsultationSubSection({
  subSection,
  fieldValues,
  handleFieldValueChange,
}) {
  const dieticianSubSection = useMemo(() => {
    return subSection
  }, [subSection])

  // Use custom hook for field mapping
  const fields = useFieldMap(dieticianSubSection)

  // Use custom hooks for loading options
  const loadDieticians = useLoadOptions(fields.referToDieticianId, 'searchDieticians', 50)
  const loadNutritionFocus = useLoadOptions(fields.nutritionFocusId, 'searchNutritionFocus', 50)

  return (
    <FormGrid>
      {/* Reason Field */}
      <Label>{fields.reason?.label}</Label>
      <StyledTextarea
        value={fieldValues?.reason?.valueString || ''}
        onChange={(e) =>
          handleFieldValueChange(
            (prev) => ({
              ...prev,
              valueString: e.target.value,
            }),
            'reason',
          )
        }
        placeholder={fields.reason?.placeholder}
      />

      {/* Nutrition Focus - Static Dropdown */}
      <Label>{fields.nutritionFocusId?.label}</Label>
      <StyledAsyncSelect
        classNamePrefix="react-select"
        value={
          fieldValues?.nutritionFocusId?.code
            ? {
                value: fieldValues.nutritionFocusId.code,
                label: fieldValues.nutritionFocusId.display || fieldValues.nutritionFocusId.code,
              }
            : null
        }
        onChange={(selected) => {
          handleFieldValueChange(
            (prev) => ({
              ...prev,
              code: selected?.value || '',
              display: selected?.label || '',
            }),
            'nutritionFocusId',
          )
        }}
        loadOptions={loadNutritionFocus}
        debounceTimeout={300}
        placeholder={fields.nutritionFocusId?.placeholder || 'Search nutrition focus...'}
        isClearable
        additional={{ page: 1 }}
      />

      {/* Sessions - Number with Presets */}
      <Label>{fields.sessions?.label}</Label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <StyledInput
          type="number"
          min={fields.sessions?.min || 1}
          max={fields.sessions?.max || 12}
          value={fieldValues?.sessions?.valueNumber || 3}
          onChange={(e) =>
            handleFieldValueChange(
              (prev) => ({
                ...prev,
                valueNumber: parseInt(e.target.value) || 3,
              }),
              'sessions',
            )
          }
          width="80px"
        />
        <span>sessions</span>

        {/* Preset Buttons */}
        {[
          { value: 3, label: '3' },
          { value: 6, label: '6' },
        ]?.map((count) => (
          <PresetButton
            key={count?.value}
            type="button"
            onClick={() =>
              handleFieldValueChange(
                (prev) => ({
                  ...prev,
                  valueNumber: count.value,
                }),
                'sessions',
              )
            }
          >
            {count?.label}
          </PresetButton>
        ))}
      </div>

      {/* Refer To - Async Select for Dieticians */}
      <Label>{fields.referToDieticianId?.label}</Label>
      <StyledAsyncSelect
        classNamePrefix="react-select"
        value={
          fieldValues?.referToDieticianId?.code
            ? {
                value: fieldValues.referToDieticianId.code,
                label:
                  fieldValues.referToDieticianId.display || fieldValues.referToDieticianId.code,
              }
            : null
        }
        onChange={(selected) => {
          handleFieldValueChange(
            (prev) => ({
              ...prev,
              code: selected?.value || '',
              display: selected?.label || '',
            }),
            'referToDieticianId',
          )
        }}
        loadOptions={loadDieticians}
        debounceTimeout={300}
        placeholder={fields.referToDieticianId?.placeholder || 'Search dieticians...'}
        isClearable
        additional={{ page: 1 }}
      />

      {/* Notes Field */}
      <Label>{fields.notes?.label}</Label>
      <StyledTextarea
        value={fieldValues?.notes?.valueString || ''}
        onChange={(e) =>
          handleFieldValueChange(
            (prev) => ({
              ...prev,
              valueString: e.target.value,
            }),
            'notes',
          )
        }
        placeholder={fields.notes?.placeholder}
      />
    </FormGrid>
  )
}
